<template>
    <admin-page :result="result" description="Acceso total, plan forzado o tope de alumnos, sin pasar por Stripe">
        <kimsa-text-field class="search-field" @change="onSearch" :value="search" label="Buscar entrenador o empresa"
            placeholder="Nombre o email" prepend-inner-icon="mdi-magnify" dense hide-details />

        <div v-if="searching" class="access-hint">Buscando...</div>

        <div v-else-if="results.length" class="access-results">
            <div v-for="user in results" :key="user.objectID" class="access-row"
                :class="{ 'access-row--active': selected && selected.objectID === user.objectID }"
                @click="selectUser(user)">
                <student-avatar :avatar="user.avatar" :name="user.name || user.email" :avatar-key="user.objectID"
                    :size="34" />
                <div class="access-row-body">
                    <div class="access-row-name">{{ user.name || 'Sin nombre' }}</div>
                    <div class="access-row-email">{{ user.email }}</div>
                </div>
                <span class="access-row-type">{{ accountTypeLabel(user.accountType) }}</span>
            </div>
        </div>

        <div v-else-if="search && !searching" class="access-hint">Sin resultados.</div>

        <template v-if="selected">
            <div class="access-panel">
                <div class="access-panel-head">
                    <div class="access-panel-body">
                        <div class="access-panel-name">{{ selected.name || selected.email }}</div>
                        <div class="access-panel-sub">
                            {{ accountTypeLabel(selected.accountType) }} · Plan actual: {{ currentPlanLabel }}
                        </div>
                    </div>
                    <button-tooltip tooltip="Elegir otra cuenta">
                        <template #activator="{ on, attrs }">
                            <button class="access-clear" v-on="on" v-bind="attrs" @click="clearSelection">
                                <v-icon size="16">mdi-close</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                </div>

                <div class="access-option">
                    <div>
                        <div class="access-option-name">Acceso total</div>
                        <div class="access-option-desc">Habilita todas las funciones y saltea el cobro.</div>
                    </div>
                    <kimsa-toggle :value="fullAccess" @change="fullAccess = $event" />
                </div>

                <div class="access-option access-option--column">
                    <div>
                        <div class="access-option-name">Plan forzado</div>
                        <div class="access-option-desc">
                            Le da las funciones de ese plan sin suscripción. Solo planes de {{ planTypeLabel }}.
                        </div>
                    </div>
                    <kimsa-combobox class="access-select" :items="planOptions" :value="priceId" item-value="value"
                        @change="priceId = $event || null" dense solo placeholder="Sin forzar" placeholder-class
                        no-object />
                </div>

                <div class="access-option access-option--column">
                    <div>
                        <div class="access-option-name">Tope de alumnos</div>
                        <div class="access-option-desc">Vacío = el tope del plan.</div>
                    </div>
                    <kimsa-text-field class="access-input" :value="customMaxClients" input-mode="numeric"
                        @change="customMaxClients = $event" dense placeholder="Sin tope propio" />
                </div>

                <v-btn color="primary" class="black--text access-btn mt-4" depressed block :loading="loading"
                    @click="$refs.confirm.show()">
                    Guardar accesos
                </v-btn>
            </div>

            <kimsa-confirm ref="confirm" show-cancel title="Guardar accesos" :content="confirmContent"
                :persistent="false" :close-on-confirm="false" :loading="loading" @confirm="saveOverrides" />
        </template>
    </admin-page>
</template>

<script>
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaCombobox from '@/components/common/inputs/KimsaCombobox.vue'
import KimsaToggle from '@/components/common/inputs/KimsaToggle.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import StudentAvatar from '@/components/common/StudentAvatar.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { UserIndex } from '@/utils/Algolia'
import { db } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'

const ACCOUNT_TYPES = {
    coach: 'Entrenador',
    coach_company: 'Entrenador de empresa',
    company: 'Empresa',
}

const BILLING_ACCOUNTS_FILTER = 'accountType:coach OR accountType:coach_company OR accountType:company'

export default {
    name: 'AdminUserAccess',
    components: { KimsaTextField, KimsaCombobox, KimsaToggle, KimsaConfirm, StudentAvatar, ButtonTooltip, AdminPage },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            search: '',
            results: [],
            allPlans: [],
            selected: null,
            fullAccess: false,
            priceId: null,
            customMaxClients: '',
            searching: false,
            loading: false,
            result: null,
            searchTimer: null,
        }
    },
    computed: {
        planType(vm = this) {
            return vm.selected?.accountType === 'company' ? 'company' : 'coach'
        },
        planTypeLabel(vm = this) {
            return vm.planType === 'company' ? 'empresa' : 'entrenador'
        },
        planOptions(vm = this) {
            const options = vm.allPlans
                .filter(plan => plan.type === vm.planType)
                .map(plan => ({ name: vm.planLabel(plan.priceId), value: plan.priceId }))
            if (vm.priceId && !options.some(option => option.value === vm.priceId)) {
                options.push({ name: vm.planLabel(vm.priceId), value: vm.priceId })
            }
            return [{ name: 'Sin forzar', value: null }, ...options]
        },
        currentPlanLabel(vm = this) {
            const priceId = vm.selected?.overrides?.priceId
                || vm.selected?.stripeAccount?.currentSubscription?.priceId
                || null
            if (!priceId) return 'sin plan'
            const plan = vm.allPlans.find(p => p.priceId === priceId)
            return plan?.name || priceId
        },
        confirmContent(vm = this) {
            const lines = [
                `Acceso total: <b>${vm.fullAccess ? 'sí' : 'no'}</b>`,
                `Plan forzado: <b>${vm.priceId || 'ninguno'}</b>`,
                `Tope de alumnos: <b>${vm.customMaxClients || 'el del plan'}</b>`,
            ]
            return `Vas a guardar los accesos de <b>${vm.selected?.email}</b>.<br>${lines.join('<br>')}`
        },
    },
    async mounted() {
        const vm = this
        await vm.loadPlans()

        const email = vm.$route.query.email
        if (!email) return
        vm.search = email
        await vm.searchUsers()
        const hit = vm.results.find(user => user.email === email)
        if (hit) await vm.selectUser(hit)
    },
    methods: {
        accountTypeLabel(type) {
            return ACCOUNT_TYPES[type] || 'Desconocido'
        },
        planLabel(priceId) {
            const plan = this.allPlans.find(p => p.priceId === priceId)
            return plan ? `${plan.name} (${plan.priceId})` : priceId
        },
        async loadPlans() {
            const vm = this
            const { docs } = await db.collection('plans').get()
            vm.allPlans = docs
                .map(doc => doc.data())
                .filter(plan => plan.active === true && !!plan.priceId)
                .sort((a, b) => (a.amount || 0) - (b.amount || 0))
        },
        onSearch(value) {
            const vm = this
            vm.search = value
            vm.result = null
            if (vm.searchTimer) clearTimeout(vm.searchTimer)
            if (!value) {
                vm.results = []
                vm.selected = null
                return
            }
            vm.searchTimer = setTimeout(vm.searchUsers, 350)
        },
        clearSelection() {
            const vm = this
            vm.selected = null
            vm.search = ''
            vm.results = []
            vm.result = null
        },
        async searchUsers() {
            const vm = this
            await vm.withLoading(async () => {
                const { hits } = await UserIndex.search(vm.search, {
                    hitsPerPage: 8,
                    filters: BILLING_ACCOUNTS_FILTER,
                })
                vm.results = hits
            }, 'searching')
        },
        async selectUser(user) {
            const vm = this
            vm.result = null
            const doc = await db.collection('users').doc(user.objectID).get()
            const data = doc.exists ? doc.data() : {}
            vm.selected = { ...user, ...data, objectID: user.objectID }
            vm.fullAccess = data.overrides?.fullAccess === true
            vm.priceId = data.overrides?.priceId || null
            vm.customMaxClients = data.overrides?.customMaxClients ? String(data.overrides.customMaxClients) : ''
        },
        async saveOverrides() {
            const vm = this
            vm.loading = true
            vm.result = null
            try {
                const maxClients = Number(vm.customMaxClients)
                const overrides = {
                    fullAccess: vm.fullAccess,
                    priceId: vm.priceId || null,
                    customMaxClients: maxClients > 0 ? maxClients : null,
                }
                await db.collection('users').doc(vm.selected.objectID).update({
                    'overrides.fullAccess': overrides.fullAccess,
                    'overrides.priceId': overrides.priceId,
                    'overrides.customMaxClients': overrides.customMaxClients,
                })
                vm.selected = { ...vm.selected, overrides: { ...vm.selected.overrides, ...overrides } }
                vm.result = {
                    type: 'success',
                    message: `Accesos actualizados para ${vm.selected.email}.`
                }
            } catch (err) {
                console.error('[adminUserAccess] error', err)
                vm.result = {
                    type: 'error',
                    message: err.message || 'Error al guardar los accesos.'
                }
            } finally {
                vm.loading = false
                vm.$refs.confirm?.hide()
            }
        },
    },
}
</script>

<style scoped>
.search-field {
    max-width: 420px;
}

.access-hint {
    margin-top: 14px;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.access-results {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.access-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    cursor: pointer;
}

.access-row:hover {
    border-color: var(--v-txtMuted-base);
}

.access-row--active {
    border-color: var(--v-accent-base);
    background: rgba(51, 153, 153, 0.08);
}

.access-row-body {
    flex: 1;
    min-width: 0;
}

.access-row-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.access-row-email {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.access-row-type {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    white-space: nowrap;
}

.access-panel {
    margin-top: 20px;
    padding: 16px;
    border-radius: 12px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.access-panel-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--v-outline-base);
}

.access-panel-body {
    flex: 1;
    min-width: 0;
}

.access-clear {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--v-txtMuted-base);
    border: 1px solid var(--v-outline2-base);
}

.access-clear:hover {
    color: var(--v-txtPrimary-base);
    background: var(--v-surface-base);
}

.access-panel-name {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.access-panel-sub {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.access-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.access-option--column {
    align-items: flex-end;
}

.access-option-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.access-option-desc {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.access-select,
.access-input {
    width: 220px;
    flex-shrink: 0;
}

.access-btn {
    height: 40px !important;
    border-radius: 8px;
}

@media (max-width: 600px) {
    .access-option {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .access-select,
    .access-input {
        width: 100%;
    }
}
</style>
